import React from "react";
import { observer } from "mobx-react";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindText from "library/components/_tailwind/text";
import TailwindTranslatedText from "library/components/_tailwind/translated-text";
import TailwindAnchor from "library/components/_tailwind/anchor";
import TailwindList, {
  TailwindListType,
} from "library/components/_tailwind/list";
import TailwindListItem from "library/components/_tailwind/list/list-item";
import config from "core/config";
import { BroadcastEligibility } from "common/broadcast/_stores/broadcast/types";

type Props = {
  eligibility: BroadcastEligibility | null;
};

/**
 * BCAMS-5768: shown instead of the broadcaster when Perl says the stream may
 * not go live yet (no approved profile photo, or a stream announcement such as
 * the Diamond fan club video is still unfulfilled after its grace period).
 * Mirrors the gate the new model center applies to its Go Live button.
 */
const BroadcastNotEligibleError: React.ComponentType<Props> = ({
  eligibility,
}) => {
  const missingPhoto = eligibility?.has_profile_image === false;
  const blocking = eligibility?.blocking_announcements || [];

  return (
    <TailwindFlex
      height={"h-full"}
      width={"w-full"}
      flexDirection={"flex-col"}
      alignItems={"items-center"}
      justifyContent={"justify-center"}>
      <TailwindFlex
        width={"w-full"}
        maxWidth={"max-w-3xl"}
        flexDirection={"flex-col"}
        alignItems={"items-center"}
        justifyContent={"justify-center"}>
        <TailwindTranslatedText
          fontSize={"text-xl"}
          margin={["mb-2"]}
          fontWeight={"font-bold"}
          textAlign={"text-center"}
          descriptor={{
            id: "broadcast-not-eligible.title",
            defaultMessage: "You can't go live yet",
          }}
        />
        <TailwindText margin={["mb-4"]} textAlign={"text-center"}>
          <TailwindTranslatedText
            descriptor={{
              id: "broadcast-not-eligible.body",
              defaultMessage:
                "Before you can broadcast, the following needs your attention:",
            }}
          />
        </TailwindText>
        <TailwindList type={TailwindListType.ul} padding={["pl-4"]} margin={["mb-6"]}>
          {missingPhoto && (
            <TailwindListItem margin={["ml-4"]}>
              <TailwindAnchor
                to={`${config.newModelCenterUrl}/models/photos`}
                openInNewTab>
                <TailwindTranslatedText
                  descriptor={{
                    id: "broadcast-not-eligible.missing-photo",
                    defaultMessage:
                      "Upload a profile photo and wait for it to be approved.",
                  }}
                />
              </TailwindAnchor>
            </TailwindListItem>
          )}
          {blocking.map(announcement => (
            <TailwindListItem
              key={announcement.announcement_id}
              margin={["ml-4"]}>
              <TailwindAnchor
                to={`${config.newModelCenterUrl}/models/home`}
                openInNewTab>
                {announcement.title || announcement.name}
              </TailwindAnchor>
            </TailwindListItem>
          ))}
        </TailwindList>
        <TailwindText textAlign={"text-center"}>
          <TailwindTranslatedText
            descriptor={{
              id: "broadcast-not-eligible.go-to-model-center",
              defaultMessage:
                "Complete these in the {model_center} and then come back to go live.",
            }}
            values={{
              model_center: `<a href="${config.newModelCenterUrl}/models/home" target="_blank" rel="noopener noreferrer"><strong>Model Center</strong></a>`,
            }}
          />
        </TailwindText>
      </TailwindFlex>
    </TailwindFlex>
  );
};

export default observer(BroadcastNotEligibleError);
